<script lang="ts">
  /**
   * MobileTopBar - the page header on phones (<768px).
   * Title (+ grey subtitle), optional back link, right-side icon actions with a
   * search button by default, and an optional second row (segmented control,
   * chips). No logo, nav dropdown or avatar: the bottom tab bar and More cover those.
   * Contract: plans/mobile-v2.md §3.3.
   *
   * Action snippets can use the `mtb-icon-btn` class for 44×44 icon buttons.
   */
  import type { Snippet } from 'svelte';
  import { Link } from '@inertiajs/svelte';
  import { openGlobalSearch } from '../../lib/stores/globalSearch';

  interface Props {
    /** Never truncated; wraps to a second line when long. */
    title: string;
    /** Grey, after the title. Hidden first when the row runs out of space. */
    subtitle?: string;
    /** Renders ‹ before the title. A string is an href (Inertia Link). */
    back?: string | (() => void);
    /** Accessible name of the back control, e.g. "Back to Projects". */
    backLabel?: string;
    /** Search button that opens global search. Default true. */
    showSearch?: boolean;
    /** Right-side actions, rendered after the search button. */
    actions?: Snippet;
    /** Second row: segmented control, chips, pills. */
    below?: Snippet;
  }

  let {
    title,
    subtitle,
    back,
    backLabel = 'Back',
    showSearch = true,
    actions,
    below,
  }: Props = $props();
</script>

<header class="mtb">
  <div class="mtb-row">
    {#if typeof back === 'string'}
      <Link href={back} class="mtb-back" aria-label={backLabel}>
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
          ><path d="m15 5-7 7 7 7" /></svg
        >
      </Link>
    {:else if back}
      <button type="button" class="mtb-back" aria-label={backLabel} onclick={back}>
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
          ><path d="m15 5-7 7 7 7" /></svg
        >
      </button>
    {/if}

    <h1 class="mtb-heading">
      <span class="mtb-title">{title}</span>
      {#if subtitle}
        <!-- The inner span wraps onto a clipped second line when it doesn't fit. -->
        <span class="mtb-subtitle"><span>{subtitle}</span></span>
      {/if}
    </h1>

    {#if showSearch || actions}
      <div class="mtb-actions">
        {#if showSearch}
          <button type="button" class="mtb-icon-btn" aria-label="Search" onclick={openGlobalSearch}>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-4-4" />
            </svg>
          </button>
        {/if}
        {@render actions?.()}
      </div>
    {/if}
  </div>

  {#if below}
    <div class="mtb-below">
      {@render below()}
    </div>
  {/if}
</header>

<style>
  .mtb {
    position: sticky;
    top: 0;
    z-index: 40;
    flex-shrink: 0;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
    /* Clear the status bar/notch in standalone mode (0 in a Safari tab) */
    padding-top: var(--safe-top);
  }

  .mtb-row {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 52px;
    padding: 4px 8px 4px 16px;
  }

  .mtb-heading {
    display: flex;
    flex: 1 1 auto;
    align-items: baseline;
    min-width: 0;
    margin: 0;
    padding: 4px 0;
  }

  .mtb-title {
    flex: 0 1 auto;
    min-width: 0;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.01em;
    color: #111827;
    overflow-wrap: anywhere;
  }

  /* Leftover-space approach: the subtitle has a zero flex-basis, so it only
     gets the width the title doesn't need and the title wraps only when it
     can't fit on its own. Its text sits after a zero-width ::before and wraps
     onto a hidden second line once it no longer fits the leftover, so it
     disappears whole instead of being clipped mid-word. */
  .mtb-subtitle {
    display: flex;
    flex: 1 1 0;
    flex-wrap: wrap;
    min-width: 0;
    height: 1.2em;
    overflow: hidden;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.2;
    color: #6b7280;
    white-space: nowrap;
  }

  /* Full-height so the first flex line is one text line tall and the wrapped
     text lands below the clip. */
  .mtb-subtitle::before {
    content: '';
    width: 0;
    height: 1.2em;
  }

  /* The gap lives inside the hidden text, so a dropped subtitle frees it too. */
  .mtb-subtitle > span {
    padding-left: 6px;
  }

  .mtb-actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 2px;
  }

  .mtb :global(.mtb-back) {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 44px;
    height: 44px;
    margin-left: -14px;
    margin-right: -6px;
    padding: 0;
    border: 0;
    background: none;
    color: #2563eb;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .mtb :global(.mtb-back svg) {
    width: 26px;
    height: 26px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.25;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  /* Shared 44×44 icon button for the actions snippet. */
  .mtb :global(.mtb-icon-btn) {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    border-radius: 12px;
    background: none;
    color: #4b5563;
    font: inherit;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .mtb :global(.mtb-icon-btn:active) {
    background: #f3f4f6;
  }

  .mtb :global(.mtb-icon-btn svg) {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .mtb-below {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 12px 8px;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .mtb-below::-webkit-scrollbar {
    display: none;
  }
</style>
